import React, { Fragment, useState, useEffect, useCallback } from "react";
import { push } from "connected-react-router";
import moment from "moment";
import { Button, Input, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import { toast } from "react-toastify";

import getQueryString from "./utils.js";

import { fetchCdgLoadRequests, submitCdgLoadRequests, createCdgLoadRequest, finalizeCdgLoadRequest, cancelCdgLoadRequest } from "../../actions/cdg_load_requests.js";
import CustomToolbar from "./components/CustomToolbar.js";

// Import ReactApexChart for charting
import ReactApexChart from "react-apexcharts";

const CDGLoadRequests = (props) => {
  const dispatch = useDispatch();
  const store = useSelector((state) => state.cdgLoadRequests);

  const [dataOptions, setDataOptions] = useState("");
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [totalLoad, setTotalLoad] = useState("");
  const [curtailableLoad, setCurtailableLoad] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [hideLoadingId, setHideLoadingId] = useState(null);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [curtailmentGranted, setCurtailmentGranted] = useState("");
  const [finalizeLoadingId, setFinalizeLoadingId] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoadingId, setCancelLoadingId] = useState(null);
  const [submitBidToEmc, setSubmitBidToEmc] = useState(false);
  const [price, setPrice] = useState("");
  const [powerUsageModalOpen, setPowerUsageModalOpen] = useState(false);
  const [powerChartData, setPowerChartData] = useState([]);
  const [powerChartLoading, setPowerChartLoading] = useState(false);

  const refetchData = useCallback(
    (dataOptions) => {
      dispatch(fetchCdgLoadRequests(dataOptions));
    },
    [dispatch]
  );

  useEffect(() => {
    refetchData(dataOptions);
  }, [dataOptions, refetchData]);

  // Function to generate periods
  const generatePeriods = () => {
    const periods = [];
    const now = moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();
    
    // Calculate current period (0-47)
    let currentPeriod = Math.floor(currentHour * 2 + currentMinute / 30);
    
    // If current time is after 23:30, we're at period 47
    if (currentHour === 23 && currentMinute > 30) {
      currentPeriod = 47;
    }
    
    // Start 3 periods after current period
    const startIndex = (currentPeriod + 3) % 48;
    
    // Generate next 12 periods
    for (let i = 0; i < 3; i++) {
      let period = ((startIndex + i) % 48) + 1;
      if (currentMinute > 54 || (currentMinute > 24 && currentMinute < 30)) {
        period += 1;
      }
      const isTomorrow = period < startIndex;
      let label;
      if (isTomorrow) {
        label = `${period} - Tomorrow`;
      } else {
        label = `${period}`;
      }
      periods.push({
        value: period,
        label: label
      });
    }
    
    return periods;
  };

  const handleAddItem = () => {
    // dispatch(push(`/resources/cdg_load_requests/add`));
  };

  const handleHide = async (id) => {
    try {
      setHideLoadingId(id);
      await axios.post(`/cdg_load_requests/${id}/hide`);
      toast.success("Load request hidden successfully");
      // Refetch data to update the table
      refetchData(dataOptions);
    } catch (error) {
      toast.error("Failed to hide load request");
    } finally {
      setHideLoadingId(null);
    }
  };

  const columns = [
    {
      label: "ID",
      name: "id",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      label: "Offer ID",
      name: "offer_id",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          style: { width: "100px" }
        }),
      },
    },
    {
      name: "date",
      label: "Date",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { minWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return value ? moment(value).format("DD MMM YYYY") : "-";
        },
      },
    },
    {
      label: "Period",
      name: "period",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "datetime",
      label: "Datetime",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { minWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return value ? moment(value).format("DD MMM YYYY HH:mm:ss") : "-";
        },
      },
    },
    {
      name: "valid_until",
      label: "Valid Until",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { minWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return value ? moment(value).format("DD MMM YYYY HH:mm:ss") : "-";
        },
      },
    },
    {
      label: "Status",
      name: "status",
      options: {
        filter: true,
        sort: true,
        filterType: "checkbox",
        filterOptions: {
          names: ["Hidden", "Inquired", "Pending", "Finalized", "Cancelled"],
        },
        customFilterListOptions: {
          render: (v) => {
            if (v.length > 0) {
              return `STATUS: ${v}`;
            }
            return [];
          },
          update: (filterList, filterPos, index) => {
            if (filterPos === 0) {
              filterList[index].splice(filterPos, 1, "");
            } else if (filterPos === 1) {
              filterList[index].splice(filterPos, 1);
            } else if (filterPos === -1) {
              filterList[index] = [];
            }

            return filterList;
          },
        },
        customBodyRender: (value, tableMeta, updateValue) => {
          if (!value) return "-";
          // Capitalize the first letter of the status value
          return value.charAt(0).toUpperCase() + value.slice(1);
        }
      },
    },
    {
      label: "Forecasted Total",
      name: "total_load",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { maxWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <div style={{ textAlign: 'center' }}>{value ? value : '-'}</div>;
        }
      },
    },
    {
      label: "Forecasted Curtailable",
      name: "curtailable_load",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { maxWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <div style={{ textAlign: 'center' }}>{value ? value : '-'}</div>;
        }
      },
    },
    {
      label: "Requested Total",
      name: "req_total_load",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { maxWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <div style={{ textAlign: 'center' }}>{value ? value : '-'}</div>;
        }
      },
    },
    {
      label: "Requested Curtailable",
      name: "req_curtailable_load",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { maxWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <div style={{ textAlign: 'center' }}>{value ? value : '-'}</div>;
        }
      },
    },
    {
      label: "Granted Load",
      name: "granted_load",
      options: {
        filter: false,
        sort: true,
        setCellHeaderProps: () => ({
          style: { maxWidth: "120px" }
        }),
        customBodyRender: (value, tableMeta, updateValue) => {
          return <div style={{ textAlign: 'center' }}>{value ? value : '-'}</div>;
        }
      },
    },
    {
      name: "ACTIONS",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex, rowIndex) => {
          const rowData = store.cdg_load_requests[dataIndex];
          const status = rowData.status;
          
          // Determine which buttons to show based on status
          const buttons = [];
          
          if (status === "inquired") {
            // Show only Create button
            buttons.push(
              <Button
                key="create"
                className="btn-rounded mb-2"
                color="success"
                size="sm"
                onClick={() => {
                  // Open modal with the ID
                  setCurrentId(rowData.id);
                  setModalOpen(true);
                }}
              >
                Create
              </Button>
            );
          } else if (status === "pending") {
            // Show Finalize and Cancel buttons
            buttons.push(
              <Button
                key="finalize"
                className="btn-rounded mb-2"
                color="warning"
                size="sm"
                onClick={() => {
                  // Open finalize modal with the ID
                  setCurrentId(rowData.id);
                  setFinalizeModalOpen(true);
                  setCurtailmentGranted("");
                }}
              >
                Finalize
              </Button>
            );
            buttons.push(
              <Button
                key="cancel"
                className="btn-rounded mb-2"
                color="danger"
                size="sm"
                onClick={() => {
                  // Open cancel confirmation modal
                  setCurrentId(rowData.id);
                  setCancelModalOpen(true);
                }}
              >
                Cancel
              </Button>
            );
          }
          
          // Show Hide button only if status is not "pending", "finalized" or "cancelled"
          if (status !== "pending" && status !== "finalized" && status !== "cancelled") {
            buttons.push(
              <Button
                key="hide"
                className="btn-rounded mb-2"
                color="secondary"
                size="sm"
                onClick={() => handleHide(rowData.id)}
                disabled={hideLoadingId === rowData.id}
              >
                {hideLoadingId === rowData.id ? (
                  <span className="d-flex align-items-center justify-content-center">
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  </span>
                ) : (
                  "Hide"
                )}
              </Button>
            );
          }
          
          return (
            <div className={`d-flex flex-column`}>
              {buttons}
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    count: store.total_cdg_load_requests,
    serverSide: true,
    onTableChange: (action, tableState) => {
      switch (action) {
        case "changePage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "sort":
          if (
            sortColumns.filter(
              (sort) => sort.name === tableState.sortOrder.name
            ).length > 0
          ) {
            // update the existing sortColumn
            let index = sortColumns.findIndex(
              (sort) => sort.name === tableState.sortOrder.name
            );
            sortColumns[index] = {
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            };

            setSortColumns(sortColumns);
          } else {
            // add a new sortColumn
            sortColumns.push({
              name: tableState.sortOrder.name,
              direction: tableState.sortOrder.direction,
            });

            setSortColumns(sortColumns);
          }

          setDataOptions(getQueryString(tableState, sortColumns));
          break;
        case "changeRowsPerPage":
          setDataOptions(getQueryString(tableState, sortColumns));
          break;
      }
    },
  };

  // Generate periods for dropdown
  const periods = generatePeriods();

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handlePeriodChange = (value) => {
    if (selectedPeriod.includes(value)) {
      setSelectedPeriod(selectedPeriod.filter(period => period !== value));
    } else {
      setSelectedPeriod([...selectedPeriod, value]);
    }
  };

  const getSelectedPeriodLabels = () => {
    if (selectedPeriod.length === 0) return "Select periods";
    
    const selectedLabels = periods
      .filter(period => selectedPeriod.includes(period.value))
      .map(period => period.label);
    
    // If more than 3 items, show first 2 and count
    if (selectedLabels.length > 3) {
      return `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2} more`;
    }
    
    return selectedLabels.join(', ');
  };

  const handleSubmit = async () => {
    if (selectedPeriod.length === 0) {
      toast.warn("Please select at least one period");
      return;
    }
    
    try {
      setIsSubmitting(true);
      await dispatch(submitCdgLoadRequests(selectedPeriod));
      // Refetch data to update the table after successful submission
      refetchData(dataOptions);
      // Clear selected periods after submission
      setSelectedPeriod([]);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreate = async () => {
    // Validate inputs
    if (!totalLoad || !curtailableLoad) {
      toast.warn("Please fill in both fields");
      return;
    }
    
    try {
      setIsCreating(true);
      
      // Call the backend API to create the request
      await dispatch(createCdgLoadRequest(currentId, totalLoad, curtailableLoad, submitBidToEmc, price));
      
      // Close the modal
      setModalOpen(false);
      
      // Reset form fields
      closeModalAndClear();
      
      // Refetch data to update the table
      refetchData(dataOptions);
    } catch (error) {
    } finally {
      setIsCreating(false);
    }
  };

  const handleFinalize = async () => {
    // Validate input
    if (!curtailmentGranted) {
      toast.warn("Please enter curtailment granted value");
      return;
    }
    
    try {
      setFinalizeLoadingId(currentId);
      
      // Call the backend API to finalize the request
      await dispatch(finalizeCdgLoadRequest(currentId, curtailmentGranted));
      
      // Refetch data to update the table
      refetchData(dataOptions);
      
      // Close the modal
      setFinalizeModalOpen(false);
      setCurtailmentGranted("");
    } catch (error) {
    } finally {
      setFinalizeLoadingId(null);
    }
  };

  // Function to close modal and clear fields
  const closeModalAndClear = () => {
    setModalOpen(false);
    setTotalLoad("");
    setCurtailableLoad("");
    setSubmitBidToEmc(false);
    setPrice("");
  };

  return (
    <Fragment>
      <div className="mb-3">
        <Label for="periodSelect">Request Load Values:</Label>
        <div className="d-flex align-items-center">
          <div className="col-6 col-sm-6 col-md-2">
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="w-100">
              <DropdownToggle 
                tag="div" 
                className="d-flex align-items-center justify-content-between w-100"
                style={{ cursor: 'pointer', border: '1px solid #ced4da', borderRadius: '4px', padding: '0.375rem 0.75rem' }}
              >
                <span>{getSelectedPeriodLabels()}</span>
                <i className="fa fa-chevron-down ml-2"></i>
              </DropdownToggle>
              <DropdownMenu className="w-100" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {periods.map((period) => (
                  <DropdownItem key={period.value} toggle={false}>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`period-${period.value}`}
                        checked={selectedPeriod.includes(period.value)}
                        onChange={() => handlePeriodChange(period.value)}
                      />
                      <label className="form-check-label" htmlFor={`period-${period.value}`}>
                        {period.label}
                      </label>
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
          <div className="col-6 col-md-6 col-sm-2">
            <Button
              color="primary"
              className="mx-3"
              onClick={handleSubmit}
              disabled={selectedPeriod.length === 0 || isSubmitting}
            >
              {isSubmitting ? (
                <span className="d-flex align-items-center">
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                </span>
              ) : (
                "Submit"
              )}
            </Button>
            <Button
              color="info"
              className="mx-1"
              onClick={async () => {
                setPowerUsageModalOpen(true);
                // Fetch data when modal opens
                setPowerChartLoading(true);
                
                try {
                  // Call the backend API to get power usage data
                  const response = await axios.get("/ev_minutely_power?hours=6");
                  const powerData = response.data;
                  
                  // Transform the data to be suitable for charting
                  const chartData = powerData.map(item => ({
                    time: new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    power_kw: parseFloat(item.power_kw)
                  }));
                  
                  setPowerChartData(chartData);
                } catch (error) {
                  console.error("Error fetching power data:", error);
                  // Set some default data in case of error
                  const now = new Date();
                  const defaultData = [];
                  for (let i = 5; i >= 0; i--) {
                    const time = new Date(now);
                    time.setHours(time.getHours() - i);
                    defaultData.push({
                      time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                      power_kw: Math.floor(Math.random() * 500) + 100
                    });
                  }
                  setPowerChartData(defaultData);
                } finally {
                  setPowerChartLoading(false);
                }
              }}
            >
              Power Usage
            </Button>
          </div>
        </div>
      </div>
      <div>
        <MUIDataTable
          data={store.cdg_load_requests || []}
          columns={columns}
          options={options}
        />
      </div>
      <Modal isOpen={modalOpen} toggle={closeModalAndClear}>
        <ModalHeader toggle={closeModalAndClear}>Create Load Request</ModalHeader>
        <ModalBody>
          <div className="form-group mb-3">
            <Label for="totalLoad">Total Load</Label>
            <Input
              type="number"
              name="totalLoad"
              id="totalLoad"
              value={totalLoad}
              onChange={(e) => setTotalLoad(e.target.value)}
              placeholder="E.g. 1.2 (in MW)"
            />
          </div>
          <div className="form-group mb-3">
            <Label for="curtailableLoad">Curtailable Load</Label>
            <Input
              type="number"
              name="curtailableLoad"
              id="curtailableLoad"
              value={curtailableLoad}
              onChange={(e) => setCurtailableLoad(e.target.value)}
              placeholder="E.g. 0.1 (in MW)"
            />
          </div>
          <div className="form-check mb-1">
            <input
              type="checkbox"
              className="form-check-input"
              id="submitBidToEmc"
              checked={submitBidToEmc}
              onChange={(e) => setSubmitBidToEmc(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="submitBidToEmc">
              Submit bid to EMC
            </label>
          </div>
          {submitBidToEmc && (
            <div className="form-group">
              <Label for="price">Price</Label>
              <Input
                type="number"
                name="price"
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="E.g. 100"
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModalAndClear}>Cancel</Button>
          <Button color="primary" onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <span className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              </span>
            ) : (
              "Create"
            )}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={finalizeModalOpen} toggle={() => setFinalizeModalOpen(false)}>
        <ModalHeader toggle={() => setFinalizeModalOpen(false)}>Finalize Load Request</ModalHeader>
        <ModalBody>
          <div className="form-group">
            <Label for="curtailmentGranted">Curtailment Granted</Label>
            <Input
              type="number"
              name="curtailmentGranted"
              id="curtailmentGranted"
              value={curtailmentGranted}
              onChange={(e) => setCurtailmentGranted(e.target.value)}
              placeholder="E.g. 0.1 (in MW)"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setFinalizeModalOpen(false)}>Cancel</Button>
          <Button color="warning" onClick={handleFinalize} disabled={finalizeLoadingId !== null}>
            {finalizeLoadingId === currentId ? (
              <span className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm mr-2 text-white" role="status" aria-hidden="true"></span>
              </span>
            ) : (
              "Finalize"
            )}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={cancelModalOpen} toggle={() => setCancelModalOpen(false)}>
        <ModalHeader toggle={() => setCancelModalOpen(false)}>Confirm Cancellation</ModalHeader>
        <ModalBody>
          Are you sure you want to cancel this request?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCancelModalOpen(false)}>Close</Button>
          <Button 
            color="danger"
            onClick={async () => {
              try {
                setCancelLoadingId(currentId);
                await dispatch(cancelCdgLoadRequest(currentId));
                refetchData(dataOptions);
                setCancelModalOpen(false);
                setCancelLoadingId(null);
              } catch (error) {
                setCancelLoadingId(null);
              }
            }} 
            disabled={cancelLoadingId !== null}
          >
            {cancelLoadingId === currentId ? (
              <span className="d-flex align-items-center">
                <span className="spinner-border spinner-border-sm mr-2 text-white" role="status" aria-hidden="true"></span>
              </span>
            ) : (
              "Confirm Cancellation"
            )}
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Power Usage Modal */}
      <Modal isOpen={powerUsageModalOpen} toggle={() => setPowerUsageModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setPowerUsageModalOpen(false)}>Power Usage (Past 6 Hours)</ModalHeader>
        <ModalBody>
          {powerChartLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <div>
              {/* Chart using ReactApexChart */}
              {powerChartData.length > 0 ? (
                <div>
                  <div style={{ height: '300px' }}>
                    <ReactApexChart
                      type="line"
                      height={300}
                      options={{
                        chart: {
                          id: "cdg-power-chart",
                          toolbar: { show: false },
                          zoom: { enabled: false },
                        },
                        stroke: {
                          width: 2,
                          curve: "smooth",
                        },
                        colors: ["#8884d8"],
                        dataLabels: { enabled: false },
                        xaxis: {
                          type: "category",
                          categories: powerChartData.map(item => item.time),
                          tickAmount: 12,
                          labels: {
                            rotate: -45,
                            rotateAlways: false,
                            hideOverlappingLabels: true,
                            trim: true,
                            style: {
                              colors: "#6b859e",
                              fontSize: "12px",
                            },
                          },
                        },
                        yaxis: {
                          min: 0,
                          labels: {
                            formatter: (value) => Math.round(value) + " kW",
                            style: {
                              colors: "#6b859e",
                              fontSize: "12px",
                            },
                          },
                        },
                        grid: {
                          strokeDashArray: 4,
                        },
                        tooltip: {
                          y: {
                            formatter: (value) => value + " kW",
                          },
                        },
                      }}
                      series={[
                        {
                          name: "Power",
                          data: powerChartData.map(item => item.power_kw),
                        },
                      ]}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-center">No power usage data available</p>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPowerUsageModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default CDGLoadRequests;
